import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type Header,
  type RowData,
  type SortingState,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { useControllableState } from '../lib/useControllableState.js';
import { CountFooter } from './Pagination.js';
import { EmptyState } from './EmptyState.js';
import { SkeletonRows } from './Skeleton.js';
import { StateBadge, type ComponentState } from './StateBadge.js';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Right-align and use tabular numerals. */
    numeric?: boolean;
    /** Mono cell (IDs, timestamps). Case preserved. */
    mono?: boolean;
    /** Tint the column text. */
    tone?: 'mute' | 'brand' | 'ok' | 'warn' | 'info';
    /** Let this column absorb spare width (minmax(size, 1fr)). Defaults to the last column. */
    grow?: boolean;
    /** Extra cell class. */
    cellClassName?: string;
    /** Extra header class. */
    headerClassName?: string;
    /**
     * Pin the column to the right edge of the scrolling grid (header and cells). Pinned columns
     * must be the trailing columns; they stay visible while the rest scrolls sideways, so a block
     * of per-row controls (the Sources matrix) is never out of view.
     */
    sticky?: 'right';
  }
}

// Column definitions accept any accessor value type; TanStack's own docs type this the same way.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DataTableColumn<T> = ColumnDef<T, any>;

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Stable row key. */
  rowKey: (row: T) => string;
  /** Accessible name for the grid. */
  'aria-label': string;
  selectedKey?: string | null;
  onSelect?: (key: string, row: T) => void;
  /** Enter or double-click on a row. */
  onActivate?: (key: string, row: T) => void;
  sorting?: SortingState;
  defaultSorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Sort server-side: rows are rendered in the given order. */
  manualSorting?: boolean;
  /** Scroll container height in px, or `fill` to take the parent height (parent must be sized). Default 480. */
  height?: number | 'fill';
  /** Row height in px. Default 34 (py-2 at 12.5px). */
  rowHeight?: number;
  /** Component state; `loading` renders skeleton rows, `empty` the empty message. */
  state?: ComponentState;
  /** Detail after the state label, e.g. `people failed`. */
  stateDetail?: ReactNode;
  emptyMessage?: string;
  /** Show the built-in count footer. Default true. */
  showCount?: boolean;
  /** Noun for the count footer. */
  noun?: string;
  /** Total rows before filtering (for `N shown`). */
  total?: number;
  /** Extra footer content on the right (Pagination). */
  footerRight?: ReactNode;
  /** Per-row class (critical rows: `bg-brand/10`). */
  rowClassName?: (row: T) => string | undefined;
  /** Mark a row as critical: 10% brand tint. The 2px brand bar is reserved for the selected row. */
  isCritical?: (row: T) => boolean;
  className?: string;
}

/*
 * Column headers sit in a dense grid, so they carry less tracking than the standalone `label-mono`
 * convention (0.18em): at 0.14em the words hold together when the eye scans a row of them, and the
 * narrower run keeps headers like `CHILDREN` inside their column (readability pass 2026-10-23).
 */
const HEADER_CLASS = 'font-mono text-2xs uppercase tracking-[0.14em] font-normal text-mute';

const TONE: Record<NonNullable<'mute' | 'brand' | 'ok' | 'warn' | 'info'>, string> = {
  mute: 'text-mute',
  brand: 'text-brand-text',
  ok: 'text-ok',
  warn: 'text-warn',
  info: 'text-info',
};

/**
 * Dense generic table (DESIGN_SYSTEM section 8.6) on TanStack Table v8 + react-virtual.
 * Mono 10px headers, 12.5px rows, --hair rules, hover --card, active row 2px brand bar in the first
 * cell, no zebra. Sticky header, sortable columns, virtualised body, keyboard navigation
 * (up/down/home/end/page, enter) with aria grid roles.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  selectedKey,
  onSelect,
  onActivate,
  sorting,
  defaultSorting = [],
  onSortingChange,
  manualSorting = false,
  height = 480,
  rowHeight = 34,
  state = 'complete',
  stateDetail,
  emptyMessage = 'No rows match.',
  showCount = true,
  noun = 'rows',
  total,
  footerRight,
  rowClassName,
  isCritical,
  className,
  ...aria
}: DataTableProps<T>) {
  const [sortingState, setSorting] = useControllableState<SortingState>({ value: sorting, defaultValue: defaultSorting, onChange: onSortingChange });
  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => rowKey(row),
    state: { sorting: sortingState },
    onSortingChange: (updater) => setSorting(typeof updater === 'function' ? updater(sortingState) : updater),
    manualSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: manualSorting ? undefined : getSortedRowModel(),
    enableSortingRemoval: true,
  });

  const tableRows = table.getRowModel().rows;
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
    // keep keyboard-selected rows clear of the sticky header row
    scrollPaddingStart: rowHeight,
  });

  const gridId = useId();
  const [focusIndex, setFocusIndex] = useState<number>(() => {
    const i = selectedKey ? tableRows.findIndex((r) => r.id === selectedKey) : -1;
    return i;
  });

  // keep focus index in sync with external selection
  useEffect(() => {
    if (!selectedKey) return;
    const i = tableRows.findIndex((r) => r.id === selectedKey);
    if (i >= 0) setFocusIndex(i);
  }, [selectedKey, tableRows]);

  const leaf = table.getVisibleLeafColumns();
  const { template, minWidth, stickyRight } = useMemo(() => {
    const anyGrow = leaf.some((c) => c.columnDef.meta?.grow);
    const anySticky = leaf.some((c) => c.columnDef.meta?.sticky === 'right');
    const columns = leaf.map((c, i) => {
      const size = c.getSize();
      // Pinned columns never grow; with a pinned block the last unpinned column absorbs spare width.
      let lastFlexible = leaf.length - 1;
      if (anySticky) while (lastFlexible > 0 && leaf[lastFlexible]?.columnDef.meta?.sticky === 'right') lastFlexible -= 1;
      const grow = c.columnDef.meta?.sticky === 'right' ? false : (c.columnDef.meta?.grow ?? (!anyGrow && i === lastFlexible));
      return grow ? `minmax(${size}px, 1fr)` : `${size}px`;
    });
    // Right offset of each pinned leaf column: the widths of the pinned columns after it.
    const stickyRight = new Map<string, number>();
    let acc = 0;
    for (let i = leaf.length - 1; i >= 0; i--) {
      const c = leaf[i]!;
      if (c.columnDef.meta?.sticky !== 'right') break;
      stickyRight.set(c.id, acc);
      acc += c.getSize();
    }
    // The sum of the column widths: a narrower container scrolls sideways instead of clipping the
    // last column (the action columns of the people, invitations, API key and audit tables).
    return { template: columns.join(' '), minWidth: leaf.reduce((n, c) => n + c.getSize(), 0), stickyRight };
  }, [leaf]);
  const headerGroups = table.getHeaderGroups();
  const headerRows = headerGroups.length;
  /**
   * Sticky style for a header (leaf or group): pinned when every leaf under it is pinned. TanStack's
   * getLeafHeaders() lists the group header itself after its leaves, so only childless headers count.
   */
  const stickyStyle = (header: Header<T, unknown>): CSSProperties | undefined => {
    const leaves = header.getLeafHeaders().filter((l) => l.subHeaders.length === 0);
    if (leaves.length === 0 || !leaves.every((l) => stickyRight.has(l.column.id))) return undefined;
    return { position: 'sticky', right: stickyRight.get(leaves[leaves.length - 1]!.column.id) ?? 0, zIndex: 1 };
  };

  const selectRow = useCallback(
    (index: number, options?: { activate?: boolean }) => {
      const row = tableRows[index];
      if (!row) return;
      setFocusIndex(index);
      onSelect?.(row.id, row.original);
      if (options?.activate) onActivate?.(row.id, row.original);
      virtualizer.scrollToIndex(index, { align: 'auto' });
    },
    [onActivate, onSelect, tableRows, virtualizer],
  );

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (tableRows.length === 0) return;
    const pageSize = Math.max(1, Math.floor((scrollRef.current?.clientHeight ?? rowHeight * 10) / rowHeight) - 1);
    const last = tableRows.length - 1;
    const current = focusIndex < 0 ? -1 : focusIndex;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        selectRow(Math.min(last, current + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        selectRow(Math.max(0, current <= 0 ? 0 : current - 1));
        break;
      case 'PageDown':
        e.preventDefault();
        selectRow(Math.min(last, current + pageSize));
        break;
      case 'PageUp':
        e.preventDefault();
        selectRow(Math.max(0, current - pageSize));
        break;
      case 'Home':
        e.preventDefault();
        selectRow(0);
        break;
      case 'End':
        e.preventDefault();
        selectRow(last);
        break;
      case 'Enter':
        if (current >= 0) {
          e.preventDefault();
          selectRow(current, { activate: true });
        }
        break;
      default:
        break;
    }
  };

  const containerStyle: CSSProperties = height === 'fill' ? {} : { height, flexBasis: height };
  const virtualItems = virtualizer.getVirtualItems();
  const activeDescendant = focusIndex >= 0 && tableRows[focusIndex] ? `${gridId}-r-${focusIndex}` : undefined;
  const loading = state === 'loading';
  const showEmpty = !loading && tableRows.length === 0;

  return (
    <div className={cn('flex min-h-0 flex-col', height === 'fill' ? 'h-full' : '', className)}>
      <div
        ref={scrollRef}
        role="grid"
        aria-label={aria['aria-label']}
        aria-rowcount={tableRows.length + headerRows}
        aria-colcount={leaf.length}
        aria-busy={loading || undefined}
        aria-activedescendant={activeDescendant}
        aria-multiselectable={false}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={cn(
          'scrollbar-thin relative min-h-0 overflow-auto outline-none focus-visible:outline-1 focus-visible:outline-brand focus-visible:-outline-offset-1',
          height === 'fill' ? 'flex-1' : 'shrink-0',
        )}
        style={containerStyle}
      >
        <div role="rowgroup" className="sticky top-0 z-10 bg-panel" style={{ minWidth }}>
          {headerGroups.map((hg, rowIndex) => {
            // A row of column-group headers (TanStack `columns` nesting): placeholders for the ungrouped
            // columns stay empty and only the group cells carry a rule, so the band reads as a caption
            // over its block rather than as a second full header line.
            const groupRow = hg.headers.some((h) => h.subHeaders.length > 0);
            return (
              <div key={hg.id} role="row" aria-rowindex={rowIndex + 1} className={cn('grid', groupRow ? '' : 'border-b border-hair')} style={{ gridTemplateColumns: template }}>
                {hg.headers.map((header) =>
                  groupRow ? (
                    <GroupHeaderCell key={header.id} header={header} style={stickyStyle(header)} />
                  ) : (
                    <HeaderCell key={header.id} header={header} style={stickyStyle(header)} />
                  ),
                )}
              </div>
            );
          })}
        </div>
        {loading ? (
          <SkeletonRows rows={Math.min(12, Math.max(3, Math.floor(((height === 'fill' ? 400 : height) - 34) / rowHeight)))} />
        ) : showEmpty ? (
          <EmptyState message={emptyMessage} compact />
        ) : (
          <div role="rowgroup" className="relative w-full" style={{ height: virtualizer.getTotalSize(), minWidth }}>
            {virtualItems.map((vi) => {
              const row = tableRows[vi.index];
              if (!row) return null;
              const selected = selectedKey !== undefined && selectedKey !== null ? row.id === selectedKey : focusIndex === vi.index && Boolean(onSelect);
              const critical = isCritical?.(row.original) ?? false;
              return (
                <div
                  key={row.id}
                  id={`${gridId}-r-${vi.index}`}
                  role="row"
                  aria-rowindex={vi.index + headerRows + 1}
                  aria-selected={onSelect ? selected : undefined}
                  data-selected={selected || undefined}
                  data-critical={critical || undefined}
                  onClick={() => selectRow(vi.index)}
                  onDoubleClick={() => selectRow(vi.index, { activate: true })}
                  className={cn(
                    'group/row absolute left-0 top-0 grid w-full items-center border-b border-hair text-table text-ink transition-colors',
                    onSelect ? 'cursor-default hover:bg-card' : '',
                    selected ? 'bg-card' : critical ? 'bg-brand/10' : '',
                    rowClassName?.(row.original),
                  )}
                  style={{ gridTemplateColumns: template, height: vi.size, transform: `translateY(${vi.start}px)` }}
                >
                  {row.getVisibleCells().map((cell, ci) => {
                    const meta = cell.column.columnDef.meta;
                    const pinned = stickyRight.get(cell.column.id);
                    return (
                      <div
                        key={cell.id}
                        role="gridcell"
                        style={pinned !== undefined ? { position: 'sticky', right: pinned, zIndex: 1 } : undefined}
                        className={cn(
                          'relative min-w-0 truncate px-3',
                          ci === 0 ? 'pl-4' : '',
                          meta?.numeric ? 'text-right tabular-nums' : '',
                          meta?.mono ? 'font-mono text-xs' : '',
                          meta?.tone ? TONE[meta.tone] : '',
                          // Pinned cells paint their own ground (one step up from the table) so the scrolling
                          // columns never show through and the block reads as one band; they follow the row's
                          // hover and selection tints.
                          pinned !== undefined ? 'h-full bg-panel group-hover/row:bg-card group-data-[selected]/row:bg-card' : '',
                          meta?.cellClassName,
                        )}
                      >
                        {ci === 0 && selected ? (
                          <span aria-hidden className="absolute left-0 top-1/2 h-3 w-[2px] -translate-y-1/2 bg-brand" />
                        ) : null}
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showCount ? (
        <CountFooter
          total={total ?? rows.length}
          shown={total !== undefined ? rows.length : undefined}
          selected={selectedKey ? 1 : 0}
          noun={noun}
          right={
            <>
              {state !== 'complete' ? <StateBadge state={state} detail={stateDetail} /> : null}
              {footerRight}
            </>
          }
        />
      ) : null}
    </div>
  );
}

/** Column-group caption spanning its leaf columns; placeholders (ungrouped columns) render empty. */
function GroupHeaderCell<T>({ header, style }: { header: Header<T, unknown>; style?: CSSProperties }) {
  const meta = header.column.columnDef.meta;
  if (header.isPlaceholder || header.subHeaders.length === 0) return <div role="columnheader" aria-hidden style={{ gridColumn: `span ${header.colSpan}` }} />;
  return (
    <div role="columnheader" aria-colspan={header.colSpan} style={{ ...style, gridColumn: `span ${header.colSpan}` }} className={cn('flex min-w-0 items-center justify-center border-b border-hair bg-panel px-3 py-1', meta?.headerClassName)}>
      <span className={cn(HEADER_CLASS, 'truncate')}>{flexRender(header.column.columnDef.header, header.getContext())}</span>
    </div>
  );
}

function HeaderCell<T>({ header, style }: { header: Header<T, unknown>; style?: CSSProperties }) {
  const meta = header.column.columnDef.meta;
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const ariaSort = sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : canSort ? 'none' : undefined;
  const content = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
  const inner = (
    <>
      <span className="truncate">{content}</span>
      {canSort ? (
        sorted === 'asc' ? (
          <ArrowUp size={10} aria-hidden className="shrink-0 text-ink" />
        ) : sorted === 'desc' ? (
          <ArrowDown size={10} aria-hidden className="shrink-0 text-ink" />
        ) : (
          <ChevronsUpDown size={10} aria-hidden className="shrink-0 text-dim opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" />
        )
      ) : null}
    </>
  );
  return (
    <div
      role="columnheader"
      aria-sort={ariaSort}
      style={style}
      className={cn('flex min-w-0 items-center px-3 py-2', header.index === 0 ? 'pl-4' : '', meta?.numeric ? 'justify-end' : '', style ? 'bg-panel' : '', meta?.headerClassName)}
    >
      {canSort ? (
        <button
          type="button"
          onClick={header.column.getToggleSortingHandler()}
          className={cn(HEADER_CLASS, 'group flex min-w-0 items-center gap-1.5 rounded-sm transition-colors hover:text-ink', meta?.numeric ? 'flex-row-reverse' : '')}
          aria-label={typeof header.column.columnDef.header === 'string' ? `Sort by ${header.column.columnDef.header}` : undefined}
        >
          {inner}
        </button>
      ) : (
        <span className={cn(HEADER_CLASS, 'flex min-w-0 items-center gap-1.5')}>{inner}</span>
      )}
    </div>
  );
}
